<template>
  <div class="sheet relative overflow-hidden bg-white p-8 border border-gray-200 rounded-xl" id="request-voucher">
    <!-- Header -->
    <div class="flex justify-between items-start border-b-2 border-theme-active pb-6 mb-8">
      <div>
        <h1 class="text-3xl font-bold text-theme-active uppercase tracking-tight">{{ title }}</h1>
        <p class="text-gray-500 font-medium">Request ID: #{{ leadId }}</p>
      </div>
      <div class="text-right">
        <div class="text-xl font-bold text-gray-800">VEHITRA WORLD</div>
        <div class="text-xs text-gray-500 uppercase tracking-widest">International Ground Transportation</div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-12">
      <!-- Left Column: Service Details -->
      <div class="space-y-8">
        <section>
          <div class="flex items-center gap-3 mb-4">
            <div class="w-1 h-6 bg-theme-active"></div>
            <h2 class="text-lg font-bold text-gray-800">{{ serviceHeader }}</h2>
          </div>
          
          <div class="space-y-4 text-sm">
            <div v-if="lead.vehicleType" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Vehicle Type</span>
              <span class="text-gray-800 font-bold text-base">{{ lead.vehicleType }}</span>
            </div>

            <div v-if="lead.pickUp?.name" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Pick-up Location</span>
              <span class="text-gray-800 font-bold">{{ lead.pickUp.name }}</span>
            </div>

            <div v-if="lead.pickUp?.date" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Pick-up Date/Time</span>
              <span class="text-gray-800 font-bold">{{ lead.pickUp.date }} {{ lead.pickUp.time }}</span>
            </div>

            <div v-if="lead.dropOff?.name" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Drop-off Location</span>
              <span class="text-gray-800 font-bold">{{ lead.dropOff.name }}</span>
            </div>

            <div v-if="lead.dropOff?.date" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Drop-off Date/Time</span>
              <span class="text-gray-800 font-bold">{{ lead.dropOff.date }} {{ lead.dropOff.time }}</span>
            </div>

            <div v-if="isPlanTrip" class="mt-4 space-y-4">
               <div v-for="(item, idx) in lead.trip?.items" :key="idx" class="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div class="text-xs font-bold text-theme-active uppercase mb-2">{{ item.type }} Service</div>
                  <div class="grid grid-cols-1 gap-1">
                     <div><span class="text-gray-400 font-medium">From:</span> {{ item.pickUp.name }}</div>
                     <div v-if="item.dropOff?.name"><span class="text-gray-400 font-medium">To:</span> {{ item.dropOff.name }}</div>
                     <div><span class="text-gray-400 font-medium">When:</span> {{ item.pickUp.date }} {{ item.pickUp.time }}</div>
                     <div v-if="item.days"><span class="text-gray-400 font-medium">Duration:</span> {{ item.days }} Day(s)</div>
                  </div>
               </div>
            </div>
          </div>
        </section>

        <section v-if="lead.pickUp?.flightNumber">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-1 h-6 bg-theme-active"></div>
            <h2 class="text-lg font-bold text-gray-800">Travel Details</h2>
          </div>
          <div class="text-sm">
            <span class="text-gray-500 font-semibold uppercase text-xs">Flight Number</span>
            <span class="text-gray-800 font-bold block">{{ lead.pickUp.flightNumber }}</span>
          </div>
        </section>
      </div>

      <!-- Right Column: Guest Info -->
      <div class="space-y-8">
        <section>
          <div class="flex items-center gap-3 mb-4">
            <div class="w-1 h-6 bg-theme-active"></div>
            <h2 class="text-lg font-bold text-gray-800">Guest Information</h2>
          </div>
          
          <div class="space-y-4 text-sm">
            <div class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Name</span>
              <span class="text-gray-800 font-bold text-base">{{ lead.guest.name }}</span>
            </div>

            <div v-if="lead.guest.age" class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Age</span>
              <span class="text-gray-800 font-bold">{{ lead.guest.age }}</span>
            </div>

            <div class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Email</span>
              <span class="text-gray-800 font-bold underline">{{ lead.guest.email }}</span>
            </div>

            <div class="flex flex-col">
              <span class="text-gray-500 font-semibold uppercase text-xs">Phone</span>
              <span class="text-gray-800 font-bold">{{ lead.guest.contactNumber }}</span>
            </div>

            <div class="flex gap-4">
              <div v-if="lead.people" class="flex flex-col">
                <span class="text-gray-500 font-semibold uppercase text-xs">Pax</span>
                <span class="text-gray-800 font-bold font-mono text-base">{{ lead.people }}</span>
              </div>
              <div v-if="lead.luggage !== undefined" class="flex flex-col">
                <span class="text-gray-500 font-semibold uppercase text-xs">Luggage</span>
                <span class="text-gray-800 font-bold font-mono text-base">{{ lead.luggage }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-if="lead.guest.message">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-1 h-6 bg-theme-active"></div>
            <h2 class="text-lg font-bold text-gray-800">Special Instructions</h2>
          </div>
          <p class="text-sm text-gray-700 italic bg-gray-50 p-4 rounded-lg border-l-4 border-gray-200">
            "{{ lead.guest.message }}"
          </p>
        </section>
      </div>
    </div>

    <!-- Footer -->
    <div class="mt-16 pt-8 border-t border-gray-100 text-center">
      <p class="text-xs text-gray-400 uppercase tracking-widest font-bold mb-2">Vehitra World — On-Request Reservation</p>
      <p class="text-[10px] text-gray-300">This is a formal request for service. Our team will verify availability and contact you with a final confirmation and payment details.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  lead: any
  leadId: string | number
}>()

const isPlanTrip = computed(() => props.lead.service === 'plan_trip')

const title = computed(() => {
  switch (props.lead.service) {
    case 'car_rental': return 'Car Rental Request'
    case 'meet_greet': return 'Meet & Greet Request'
    case 'buses_coaches': return 'Bus & Coach Request'
    case 'plan_trip': return 'Custom Trip Request'
    default: return 'Booking Request'
  }
})

const serviceHeader = computed(() => {
  return isPlanTrip.value ? 'Requested Itinerary' : 'Service Details'
})
</script>

<style scoped>
.sheet {
  width: 8.5in;
  min-height: 11in;
  margin: 0 auto;
}
</style>
